import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Truck, RefreshCcw, Package, HeadphonesIcon, Star, Instagram, MessageCircle } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { fetchProducts, fetchCollectionsMeta, fetchAllReviews } from "../lib/api";
import InstagramReels from "../components/InstagramReels";

const FEATURES = [
  { icon: Sparkles, label: "Premium Craftsmanship", desc: "Hand-finished in Jaipur" },
  { icon: ShieldCheck, label: "Anti-Tarnish", desc: "Lifetime shine promise" },
  { icon: Truck, label: "Free Shipping", desc: "On orders above ₹999" },
  { icon: RefreshCcw, label: "Easy Returns", desc: "15-day hassle-free" },
  { icon: Package, label: "Premium Packaging", desc: "Gift-ready every time" },
  { icon: HeadphonesIcon, label: "Concierge Support", desc: "By Bindni AI + humans" },
];

const HERO_IMG = "https://images.pexels.com/photos/8751528/pexels-photo-8751528.jpeg";

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [meta, setMeta] = useState({ collections: [], categories: [] });
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchProducts({ collection: "bestsellers", limit: 8 }).then(setBestsellers).catch(() => {});
    fetchProducts({ collection: "newarrivals", limit: 8 }).then(setNewArrivals).catch(() => {});
    fetchCollectionsMeta().then(setMeta).catch(() => {});
    fetchAllReviews().then((r) => setReviews(r.filter(x => x.image_url).slice(0, 6))).catch(() => {});
  }, []);
  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="Bindni Jewels luxury heritage" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-maroon-deep/90 via-brand-maroon-deep/55 to-brand-maroon-deep/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-maroon-deep/50 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-14 py-16 w-full">
          <p className="label-tiny text-brand-gold mb-6 hero-text-shadow">Made For Every Bindni</p>
          <h1 className="font-serif-brand text-5xl sm:text-6xl lg:text-7xl text-brand-ivory leading-[1.05] max-w-3xl hero-text-shadow">
            Everyday <span className="italic">Luxe.</span> Effortlessly You.
          </h1>
          <p className="mt-6 text-brand-ivory/95 text-lg max-w-xl leading-relaxed hero-text-shadow">
            Heirloom-inspired, anti-tarnish jewellery for the modern woman — from Monday desks to Saturday sangeets. Curated with love by our Rajasthani artisans.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/shop" className="btn-primary bg-brand-ivory text-brand-maroon border-brand-ivory hover:bg-brand-gold hover:text-brand-charcoal hover:border-brand-gold" data-testid="hero-shop-now-btn">Shop Now</Link>
            <Link to="/shop?collection=rajputana" className="btn-outline-gold bg-brand-maroon-deep/40 backdrop-blur-sm border-brand-ivory text-brand-ivory hover:bg-brand-ivory hover:text-brand-maroon" data-testid="hero-explore-btn">Explore Collection</Link>
          </div>
        </div>
      </section>

      {/* Shop-Anywhere strip */}
      <section className="bg-brand-ivory-soft py-10 border-b border-brand-gold/20" data-testid="shop-anywhere-strip">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="label-tiny text-brand-gold">Shop the way you love</p>
            <h3 className="font-serif-brand text-2xl lg:text-3xl text-brand-charcoal mt-1">Now browse Bindni across every channel</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="https://wa.me/c/917987780925" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white text-xs tracking-[0.2em] uppercase font-medium hover:brightness-110 transition-all" data-testid="shop-on-whatsapp-btn">
              <MessageCircle size={16} strokeWidth={1.6} /> Shop on WhatsApp
            </a>
            <a href="https://www.instagram.com/bindni_jewels" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 text-white text-xs tracking-[0.2em] uppercase font-medium hover:brightness-110 transition-all" style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }} data-testid="shop-on-instagram-btn">
              <Instagram size={16} strokeWidth={1.6} /> Shop on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* Trust marquee */}
      <section className="bg-brand-ivory-soft border-y border-brand-gold/20 py-5 marquee">
        <div className="marquee-track label-tiny text-brand-muted whitespace-nowrap">
          {[..."ANTI-TARNISH · 22KT GOLD PLATED · FREE SHIPPING ₹999+ · 15-DAY RETURNS · MADE IN INDIA · HYPOALLERGENIC · PREMIUM GIFT PACKAGING · ".split(" · ")].concat(["ANTI-TARNISH · 22KT GOLD PLATED · FREE SHIPPING ₹999+ · 15-DAY RETURNS · MADE IN INDIA · HYPOALLERGENIC · PREMIUM GIFT PACKAGING".split(" · ")]).flat().map((t, i) => (
            <span key={i} className="text-brand-charcoal">{t}</span>
          ))}
        </div>
      </section>

      {/* Shop by Category */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="categories-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
          <div>
            <p className="label-tiny text-brand-gold">Curated for You</p>
            <h2 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">Shop by Category</h2>
          </div>
          <Link to="/shop" className="text-sm text-brand-maroon hover:text-brand-gold flex items-center gap-2 group">
            View all <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
          {meta.categories.concat(meta.collections.slice(0, 2)).map((c) => (
            <Link key={c.slug} to={`/shop?${meta.categories.some(x => x.slug === c.slug) ? "category" : "collection"}=${c.slug}`} className="group block" data-testid={`category-${c.slug}`}>
              <div className="aspect-square overflow-hidden bg-brand-ivory-soft">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <p className="mt-3 text-center font-serif-brand text-brand-charcoal group-hover:text-brand-maroon transition-colors duration-200">{c.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured banner - Rajputana */}
      <section className="relative">
        <div className="grid lg:grid-cols-2 min-h-[70vh]">
          <div className="bg-brand-maroon-deep text-brand-ivory p-10 lg:p-20 flex flex-col justify-center">
            <p className="label-tiny text-brand-gold">Signature</p>
            <h2 className="font-serif-brand text-4xl lg:text-6xl mt-4 leading-[1.05]">The Rajputana <span className="italic gold-text-gradient">Collection</span></h2>
            <p className="text-brand-ivory/80 mt-6 max-w-md leading-relaxed">Statement pieces hand-finished by 4th-generation Jaipuri artisans — for the sangeets, receptions and heirloom moments you'll remember forever.</p>
            <Link to="/shop?collection=rajputana" className="btn-outline-gold border-brand-gold text-brand-ivory hover:bg-brand-gold hover:text-brand-charcoal mt-10 self-start" data-testid="rajputana-banner-cta">Discover the Collection</Link>
          </div>
          <div className="relative min-h-[60vh]">
            <img src="https://images.pexels.com/photos/28941551/pexels-photo-28941551.jpeg" alt="Rajputana bridal look" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="bestsellers-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
          <div>
            <p className="label-tiny text-brand-gold mb-4">Loved by 12,000+ Bindnis</p>
            <h2 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">Best Sellers</h2>
          </div>
          <Link to="/shop?collection=bestsellers" className="text-sm text-brand-maroon hover:text-brand-gold flex items-center gap-2 group">Shop all <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-12">
          {bestsellers.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-brand-ivory-soft py-24 border-y border-brand-gold/20" data-testid="why-choose-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="label-tiny text-brand-gold">The Bindni Promise</p>
            <h2 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-3">Why Choose Bindni Jewels</h2>
            <p className="text-brand-muted mt-4">Because every woman deserves heirlooms that shine as brightly on Monday as they do on wedding day.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {FEATURES.map((f) => (
              <div key={f.label} className="text-center p-6 border border-brand-gold/25 bg-brand-ivory hover:border-brand-gold transition-colors duration-300" data-testid={`feature-${f.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <f.icon size={28} strokeWidth={1.3} className="text-brand-maroon mx-auto" />
                <p className="mt-4 font-serif-brand text-lg text-brand-charcoal">{f.label}</p>
                <p className="text-xs text-brand-muted mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured banner - Anti-Tarnish */}
      <section className="grid lg:grid-cols-5 min-h-[60vh]">
        <div className="relative lg:col-span-3 min-h-[50vh]">
          <img src="https://images.pexels.com/photos/8751924/pexels-photo-8751924.jpeg" alt="Anti tarnish everyday jewellery" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="lg:col-span-2 bg-brand-ivory p-10 lg:p-16 flex flex-col justify-center">
          <p className="label-tiny text-brand-gold">Daily Elegance</p>
          <h2 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-4 leading-tight">Anti-Tarnish Jewellery, made for real life.</h2>
          <p className="text-brand-muted mt-4 leading-relaxed">Water and sweat resistant. Wear our pieces from Monday meetings to Saturday sangeets — the shine stays.</p>
          <Link to="/shop?collection=antitarnish" className="btn-primary mt-8 self-start" data-testid="antitarnish-cta">Shop Anti-Tarnish</Link>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="new-arrivals-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
          <div>
            <p className="label-tiny text-brand-gold">Fresh from the atelier</p>
            <h2 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">New Arrivals</h2>
          </div>
          <Link to="/shop?collection=newarrivals" className="text-sm text-brand-maroon hover:text-brand-gold flex items-center gap-2 group">See all <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-12">
          {newArrivals.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-brand-maroon-deep text-brand-ivory py-24" data-testid="reviews-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="label-tiny text-brand-gold">Real Customers · Real Reviews</p>
            <h2 className="font-serif-brand text-4xl lg:text-5xl mt-3">Loved by 12,000+ Bindnis</h2>
            <div className="flex justify-center items-center gap-2 mt-4">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#C5A059" strokeWidth={0} />)}
              <span className="text-sm text-brand-ivory/80 ml-2">4.9 · from 3,240+ reviews</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(reviews.length ? reviews : [1,2,3]).slice(0, 3).map((r, i) => (
              <div key={i} className="border border-brand-gold/25 p-6 bg-brand-maroon/40">
                {r.image_url && <img src={r.image_url} alt="Customer" className="w-full h-56 object-cover mb-5" />}
                <div className="flex gap-0.5 mb-3">
                  {[...Array(r.rating || 5)].map((_, j) => <Star key={j} size={12} fill="#C5A059" strokeWidth={0} />)}
                </div>
                <p className="font-serif-brand text-xl">{r.title || "Absolutely stunning quality"}</p>
                <p className="text-sm text-brand-ivory/75 mt-3 leading-relaxed">{r.body || "The finish is stunning and the packaging feels like a heirloom itself."}</p>
                <p className="label-tiny text-brand-gold mt-5">— {r.customer_name || "Verified Customer"}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Reels */}
      <InstagramReels />

      {/* Instagram */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="instagram-section">
        <div className="flex flex-col items-center text-center mb-10">
          <Instagram size={22} strokeWidth={1.4} className="text-brand-maroon" />
          <a href="https://www.instagram.com/bindni_jewels" target="_blank" rel="noreferrer" className="label-tiny text-brand-gold mt-3 hover:text-brand-maroon">@bindni_jewels</a>
          <h2 className="font-serif-brand text-3xl lg:text-4xl text-brand-charcoal mt-2">Follow the journey</h2>
          <p className="text-sm text-brand-muted mt-2">See our real customers & new drops daily on Instagram · Shop instantly on our <a href="https://wa.me/c/917987780925" target="_blank" rel="noreferrer" className="text-brand-maroon hover:underline">WhatsApp Catalogue</a></p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            "https://images.pexels.com/photos/28976815/pexels-photo-28976815.jpeg",
            "https://images.pexels.com/photos/37485309/pexels-photo-37485309.jpeg",
            "https://images.pexels.com/photos/8751528/pexels-photo-8751528.jpeg",
            "https://images.pexels.com/photos/37601639/pexels-photo-37601639.jpeg",
            "https://images.pexels.com/photos/28941551/pexels-photo-28941551.jpeg",
            "https://images.pexels.com/photos/11495747/pexels-photo-11495747.jpeg",
          ].map((src, i) => (
            <a key={i} href="https://www.instagram.com/bindni_jewels" target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden bg-brand-ivory-soft group relative">
              <img src={src} alt="Instagram" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-brand-maroon/0 group-hover:bg-brand-maroon/40 transition-colors duration-300 flex items-center justify-center">
                <Instagram size={22} className="text-brand-ivory opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
